import {createContext, useContext, useState, useEffect} from 'react';

type PartialAtLeastOne<T, Keys extends keyof T = keyof T> = Partial<T> & { [K in Keys]: Required<Pick<T, K>> }[Keys];

interface State {
    isPublishing: boolean;
    view: 'code' | 'preview';
    editCode: string; // 全屏编辑时存储的代码
    isArtifactLoading: boolean;
    selectArtifactId: string;
    publishArtifactId: string;
    isPublishArtifactModified: boolean;
    editorRef: React.MutableRefObject<any>;
    preventTriggerChangeRef: React.MutableRefObject<boolean>;
}

interface Actions {
    setPublishing: (isPublishing: boolean) => void;
    setView: (view: 'code' | 'preview') => void;
    setEditCode: (code: string, isNeedUpdateEditorCode?: boolean) => void;
    setIsArtifactLoading: (isLoading: boolean) => void;
    setSelectArtifactId: (id: string) => void;
    setPublishArtifactId: (id: string) => void;
    setIsPublishArtifactModified: (isModified: boolean) => void;
    setEditorRef: (editor: any) => void;
    setPreventTriggerChangeRef: (val: boolean) => void;
}

type Store = State & Actions;

// === 单例状态容器 ===
let state: State = {
    isPublishing: false,
    view: 'preview',
    editCode: '',
    isArtifactLoading: false,
    selectArtifactId: '',
    publishArtifactId: '',
    isPublishArtifactModified: false,
    editorRef: {current: null},
    preventTriggerChangeRef: {current: false},
};

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const setState = (partial: Partial<State>) => {
    state = {...state, ...partial};
    listeners.forEach((listener) => listener());
};

export const store: Store = {
    get isPublishing() {
        return state.isPublishing;
    },
    get view() {
        return state.view;
    },
    get editCode() {
        return state.editCode;
    },
    get isArtifactLoading() {
        return state.isArtifactLoading;
    },
    get selectArtifactId() {
        return state.selectArtifactId;
    },
    get publishArtifactId() {
        return state.publishArtifactId;
    },
    get isPublishArtifactModified() {
        return state.isPublishArtifactModified;
    },
    get editorRef() {
        return state.editorRef;
    },
    get preventTriggerChangeRef() {
        return state.preventTriggerChangeRef;
    },
    setPublishing: (isPublishing) => {
        setState({isPublishing});
    },
    setView: (view) => {
        setState({view});
    },
    setEditCode: (code, isNeedUpdateEditorCode = true) => {
        setState({editCode: code});

        if (isNeedUpdateEditorCode) {
            const editor = store.editorRef.current;
            const model = editor?.getModel();

            if (model) {
                store.setPreventTriggerChangeRef(true);
                model.setValue(code);
                store.setPreventTriggerChangeRef(false);
            }
        }
    },
    setIsArtifactLoading: (isLoading) => {
        setState({isArtifactLoading: isLoading});
    },
    setSelectArtifactId: (id) => {
        setState({selectArtifactId: id});
    },
    setPublishArtifactId: (id) => {
        setState({publishArtifactId: id});
    },
    setIsPublishArtifactModified: (isModified) => {
        setState({isPublishArtifactModified: isModified});
    },
    setEditorRef: (editor: any) => {
        state.editorRef.current = editor;
    },
    setPreventTriggerChangeRef: (val: boolean) => {
        state.preventTriggerChangeRef.current = val;
    },
};

// eslint-disable-next-line no-underscore-dangle
(window as any).__DEV_FLEX_BUILDER_STORE__ = store;

// === Context ===
const StoreContext = createContext<Store | null>(null);

export const StoreProvider: React.FC<React.PropsWithChildren<unknown>> = ({children}) => {
    const [snapshot, setSnapshot] = useState(getSnapshot);

    useEffect(() => {
        const unsubscribe = subscribe(() => {
            setSnapshot(getSnapshot());
        });
        return () => {
            // 组件销毁时，初始化状态
            setState({
                isPublishing: false,
                view: 'preview',
                editCode: '',
                isArtifactLoading: false,
                selectArtifactId: '',
                publishArtifactId: '',
                isPublishArtifactModified: false,
            });
            unsubscribe();
        };
    }, []);

    return (
        <StoreContext.Provider value={{...snapshot, ...store}}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => {
    const ctx = useContext(StoreContext);
    if (!ctx) {
        throw new Error('useStore must be used inside StoreProvider');
    }
    return ctx;
};
